import React, { useMemo } from "react";
import { useTournamentAuth } from "../../utils/authorization";
import { LoadingSkeleton } from "./LoadingSkeleton";
import { NoDataMessage } from "./NoDataMessage";
import { BowlerAveragesTableContent } from "./BowlerAveragesTableContent";
import { useBowlerAverages } from "./useBowlerAverages";

export default function BowlerAveragesTable({ tournamentId }) {
  const { canManage, loading: tournamentAuthLoading } =
    useTournamentAuth(tournamentId);

  const {
    loading,
    error,
    tournamentBowlers,
    editingBowlerId,
    editedAverage,
    editedHasAverage,
    errorMessage,
    verifyingBowlerId,
    verifyingAll,
    handleEditClick,
    handleAverageChange,
    handleSaveClick,
    handleCancelClick,
    handleVerifyClick,
    handleVerifyAll,
    handleHasAverageChange,
    handleUseCatalogAverage,
    handleReviewCommit,
    canVerifyBowlerForEdit,
    canUnverifyBowlerForEdit,
    catalogInfo,
  } = useBowlerAverages(tournamentId, { canManage });

  const activeTournamentBowlers = useMemo(() => {
    if (!tournamentBowlers?.bowlerData) return tournamentBowlers;
    return {
      ...tournamentBowlers,
      bowlerData: tournamentBowlers.bowlerData.filter(
        (bowler) => bowler.bowlerName
      ),
    };
  }, [tournamentBowlers]);

  if (error) {
    return (
      <div className="card-board p-8 text-center">
        <h3 className="heading-display text-lg mb-2">Could not load averages</h3>
        <p className="text-brand-muted">
          Error loading tournament data. Please try again later.
        </p>
      </div>
    );
  }

  if (loading || tournamentAuthLoading) {
    return <LoadingSkeleton />;
  }

  if (
    !activeTournamentBowlers ||
    !activeTournamentBowlers.bowlerData ||
    activeTournamentBowlers.bowlerData.length === 0
  ) {
    return <NoDataMessage />;
  }

  return (
    <BowlerAveragesTableContent
      tournamentBowlers={activeTournamentBowlers}
      canManage={canManage}
      editingBowlerId={editingBowlerId}
      editedAverage={editedAverage}
      editedHasAverage={editedHasAverage}
      errorMessage={errorMessage}
      verifyingBowlerId={verifyingBowlerId}
      verifyingAll={verifyingAll}
      onEditClick={handleEditClick}
      onAverageChange={handleAverageChange}
      onSaveClick={handleSaveClick}
      onCancelClick={handleCancelClick}
      onVerifyClick={handleVerifyClick}
      onVerifyAll={handleVerifyAll}
      onHasAverageChange={handleHasAverageChange}
      onUseCatalogAverage={handleUseCatalogAverage}
      onReviewCommit={handleReviewCommit}
      catalogInfo={catalogInfo}
      canVerifyBowlerForEdit={canVerifyBowlerForEdit}
      canUnverifyBowlerForEdit={canUnverifyBowlerForEdit}
    />
  );
}
